import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type {
  PacientesMetrics,
  PacientesNoVolvieronResumen,
  PacientesRecurrencia,
  PatientJourneyDeepLinkTab,
  PatientsDefaultReason,
  PatientsNetworkRow,
  PatientsTab,
} from '@/application/metrics';
import { persistReportScope } from '@/application/metrics/metricsScope';
import { breadcrumbHref } from '@/application/navigation/breadcrumbScope';
import {
  PATIENTS_NETWORK_CONCURRENCY,
  buildPatientJourneySnapshot,
  buildPatientsNetworkRow,
  mapPool,
  parseExplicitPatientsTab,
  resolvePatientsDefaultTab,
} from '@/application/metrics';
import {
  downloadNoVolvieronExport,
  getPacientesMetrics,
  getPacientesNoVolvieronResumen,
  getPacientesRecurrencia,
} from '@/infrastructure/http/metricsApi';
import { AccessDenied } from '@/presentation/components/common/AccessDenied';
import { Breadcrumbs } from '@/presentation/components/common/Breadcrumbs';
import { PanelPageState } from '@/presentation/components/common/PanelPageState';
import { ActiveFilterChips } from '@/presentation/components/common/ActiveFilterChips';
import { FilterSheet, type FilterFieldConfig } from '@/presentation/components/common/FilterSheet';
import { firstVisibleHousePath } from '@/application/navigation/menuHouses';
import { canExportReports } from '@/application/permissions/canExportReports';
import { DomainPageHeader } from '@/presentation/components/domain/DomainPageHeader';
import { DomainScopeBar } from '@/presentation/components/domain/DomainScopeBar';
import { DomainTabs } from '@/presentation/components/domain/DomainTabs';
import { PatientJourneyPipeline } from '@/presentation/components/domain/PatientJourneyPipeline';
import { PatientsNetworkRanking } from '@/presentation/components/domain/PatientsNetworkRanking';
import { MetricKpiCard } from '@/presentation/components/metrics/MetricKpiCard';
import { NoVolvieronBlock } from '@/presentation/components/metrics/NoVolvieronBlock';
import { NoVolvieronExportControls } from '@/presentation/components/metrics/NoVolvieronExportControls';
import { RecurrenceBlock } from '@/presentation/components/metrics/RecurrenceBlock';
import { formatInteger, formatPercent } from '@/presentation/components/metrics/metricFormatters';
import { useGlobalPeriod, previousMonthOf } from '@/presentation/hooks/useGlobalPeriod';
import { useAuth } from '@/presentation/contexts/useAuth';
import { usePermissions } from '@/presentation/contexts/permissionsContextCore';
import { useClinicNames } from '@/presentation/hooks/useClinicNames';
import { LoadingBlock } from '@/presentation/components/common/LoadingBlock';

/** Dominio pacientes: journey en overview; tabs profundizan sin contradecir. */
export function PatientsPage() {
  const { t, i18n } = useTranslation();
  const { token, user } = useAuth();
  const { hasPermission } = usePermissions();
  const clinicNames = useClinicNames();
  const [searchParams, setSearchParams] = useSearchParams();
  const { period, year, month, label: periodLabel } = useGlobalPeriod();
  const locale = i18n.language;

  const tenantIds = useMemo(
    () => user?.scope.allowedTenantIds ?? [],
    [user?.scope.allowedTenantIds]
  );
  const tenantKey = tenantIds.join('|');
  const multiClinic = tenantIds.length > 1;
  const clinicOptions = useMemo(
    () => tenantIds.map((id) => ({ value: id, label: clinicNames[id] ?? id })),
    [tenantIds, clinicNames]
  );

  const tenantFromUrl = searchParams.get('tenant') ?? '';
  const hasTenantInUrl = Boolean(tenantFromUrl && tenantIds.includes(tenantFromUrl));
  const [clinicId, setClinicId] = useState(() => tenantFromUrl || tenantIds[0] || '');
  const explicitTab = parseExplicitPatientsTab(searchParams.get('tab'), multiClinic);
  const [tab, setTab] = useState<PatientsTab | null>(
    () => explicitTab ?? (tenantIds.length > 1 && !tenantFromUrl ? 'network' : null)
  );
  const [defaultReason, setDefaultReason] = useState<PatientsDefaultReason | null>(
    explicitTab ? 'url' : null
  );
  const [pacientes, setPacientes] = useState<PacientesMetrics | null>(null);
  const [recurrencia, setRecurrencia] = useState<PacientesRecurrencia | null>(null);
  const [prevRecurrencia, setPrevRecurrencia] = useState<PacientesRecurrencia | null>(null);
  const [noVolvieron, setNoVolvieron] = useState<PacientesNoVolvieronResumen | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedScope, setLoadedScope] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [networkRows, setNetworkRows] = useState<PatientsNetworkRow[]>([]);
  const [networkLoading, setNetworkLoading] = useState(true);

  const canPacientes = hasPermission('metrics.pacientes');
  const canRecurrence = hasPermission('metrics.recurrencia');
  const canNoReturn = hasPermission('metrics.no_volvieron');
  const canExportRisk = canNoReturn && canExportReports(hasPermission);
  const canView = canPacientes || canRecurrence || canNoReturn;

  const patchSearchParams = useCallback(
    (patch: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (value === null || value === '') next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleTabChange = useCallback(
    (id: string) => {
      const next = parseExplicitPatientsTab(id, multiClinic) ?? 'overview';
      setTab(next);
      setDefaultReason('url');
      patchSearchParams({
        tab: next,
        tenant: next === 'network' ? null : clinicId || null,
      });
    },
    [patchSearchParams, multiClinic, clinicId]
  );

  const handleOpenNetworkClinic = useCallback(
    (tenantId: string) => {
      setClinicId(tenantId);
      setTab('risk');
      setDefaultReason('url');
      patchSearchParams({ tenant: tenantId, tab: 'risk' });
    },
    [patchSearchParams]
  );

  const handleRiskExport = useCallback(
    async (format: 'xlsx' | 'csv') => {
      if (!token || !clinicId) return;
      await downloadNoVolvieronExport(
        token,
        {
          tenantId: clinicId,
          year,
          month,
          desde: noVolvieron?.desde,
          hasta: noVolvieron?.hasta,
        },
        format
      );
    },
    [token, clinicId, year, month, noVolvieron?.desde, noVolvieron?.hasta]
  );

  const isNetworkMode =
    multiClinic &&
    (explicitTab === 'network' || tab === 'network' || (!explicitTab && !hasTenantInUrl));

  const handleClinicChange = useCallback(
    (id: string) => {
      setClinicId(id);
      patchSearchParams({ tenant: id || null });
    },
    [patchSearchParams]
  );

  const filterFields = useMemo<FilterFieldConfig[]>(
    () => [
      {
        id: 'clinic',
        type: 'select',
        label: t('domain.clinicFilter'),
        options: clinicOptions,
      },
    ],
    [clinicOptions, t]
  );

  const defaultClinicId = clinicOptions[0]?.value ?? '';
  const activeFilterChips = useMemo(() => {
    if (!clinicId) return [];
    const label = clinicOptions.find((o) => o.value === clinicId)?.label ?? clinicId;
    return [
      {
        id: 'clinic',
        label: `${t('domain.clinicFilter')}: ${label}`,
        onClear: () => handleClinicChange(defaultClinicId),
      },
    ];
  }, [clinicId, clinicOptions, defaultClinicId, handleClinicChange, t]);

  useEffect(() => {
    if (explicitTab) {
      setTab(explicitTab);
      setDefaultReason('url');
    }
  }, [explicitTab]);

  useEffect(() => {
    if (explicitTab) return;
    if (multiClinic && !hasTenantInUrl) {
      setTab('network');
      setDefaultReason('network');
      return;
    }
    setTab(null);
    setDefaultReason(null);
    setBannerDismissed(false);
  }, [clinicId, period, explicitTab, multiClinic, hasTenantInUrl]);

  useEffect(() => {
    if (tenantFromUrl && tenantIds.includes(tenantFromUrl)) {
      setClinicId(tenantFromUrl);
    } else if (!clinicId && clinicOptions[0]?.value) {
      setClinicId(clinicOptions[0].value);
    }
  }, [tenantFromUrl, tenantIds, clinicId, clinicOptions]);

  useEffect(() => {
    let cancelled = false;
    if (!token || !clinicId || !canView || isNetworkMode) {
      if (isNetworkMode) return;
      setPacientes(null);
      setRecurrencia(null);
      setPrevRecurrencia(null);
      setNoVolvieron(null);
      setLoadedScope(null);
      return;
    }
    persistReportScope(period, clinicId);
    const prev = previousMonthOf(year, month);
    const scopeKey = `${clinicId}:${period}`;

    void (async () => {
      setIsLoading(true);
      setLoadedScope(null);
      setError(null);
      try {
        const [p, r, n, rPrev] = await Promise.allSettled([
          canPacientes
            ? getPacientesMetrics(token, { tenantId: clinicId, year, month })
            : Promise.reject(new Error('skip')),
          canRecurrence
            ? getPacientesRecurrencia(token, { tenantId: clinicId, year, month })
            : Promise.reject(new Error('skip')),
          canNoReturn
            ? getPacientesNoVolvieronResumen(token, { tenantId: clinicId, year, month })
            : Promise.reject(new Error('skip')),
          canRecurrence
            ? getPacientesRecurrencia(token, {
                tenantId: clinicId,
                year: prev.year,
                month: prev.month,
              })
            : Promise.reject(new Error('skip')),
        ]);
        if (cancelled) return;
        setPacientes(p.status === 'fulfilled' ? p.value : null);
        setRecurrencia(r.status === 'fulfilled' ? r.value : null);
        setNoVolvieron(n.status === 'fulfilled' ? n.value : null);
        setPrevRecurrencia(rPrev.status === 'fulfilled' ? rPrev.value : null);
        if (p.status === 'rejected' && r.status === 'rejected' && n.status === 'rejected') {
          setError(t('metrics.states.error.message'));
        }
        setLoadedScope(scopeKey);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, clinicId, year, month, period, canView, canPacientes, canRecurrence, canNoReturn, t, isNetworkMode]);

  useEffect(() => {
    let cancelled = false;
    if (!token || !canView || !isNetworkMode) {
      return;
    }
    const networkTenantIds = tenantKey ? tenantKey.split('|') : [];
    persistReportScope(period, networkTenantIds[0] ?? '');

    void (async () => {
      setNetworkLoading(true);
      try {
        const rows = await mapPool(networkTenantIds, PATIENTS_NETWORK_CONCURRENCY, async (tenantId) => {
          const [recurrenceResult, noReturnResult] = await Promise.allSettled([
            canRecurrence
              ? getPacientesRecurrencia(token, { tenantId, year, month })
              : Promise.reject(new Error('skip')),
            canNoReturn
              ? getPacientesNoVolvieronResumen(token, { tenantId, year, month })
              : Promise.reject(new Error('skip')),
          ]);
          return buildPatientsNetworkRow({
            tenantId,
            name: tenantId,
            recurrencia: recurrenceResult.status === 'fulfilled' ? recurrenceResult.value : null,
            noVolvieron: noReturnResult.status === 'fulfilled' ? noReturnResult.value : null,
          });
        });
        if (!cancelled) setNetworkRows(rows);
      } finally {
        if (!cancelled) setNetworkLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, canView, isNetworkMode, tenantKey, year, month, period, canRecurrence, canNoReturn]);

  const namedNetworkRows = useMemo(
    () => networkRows.map((row) => ({ ...row, name: clinicNames[row.tenantId] ?? row.name })),
    [networkRows, clinicNames]
  );

  const defaultDecision = useMemo(
    () =>
      resolvePatientsDefaultTab({
        urlTab: explicitTab,
        multiClinic,
        hasTenantInUrl,
        canNoReturn,
        noReturnTotal: noVolvieron?.total ?? null,
        canRecurrence,
        tasaRecurrencia: recurrencia?.tasaRecurrencia ?? null,
        previousTasaRecurrencia: prevRecurrencia?.tasaRecurrencia ?? null,
      }),
    [
      explicitTab,
      multiClinic,
      hasTenantInUrl,
      canNoReturn,
      noVolvieron?.total,
      canRecurrence,
      recurrencia?.tasaRecurrencia,
      prevRecurrencia?.tasaRecurrencia,
    ]
  );

  useEffect(() => {
    if (explicitTab) return;
    if (defaultDecision.tab === 'network') {
      setTab('network');
      setDefaultReason('network');
      return;
    }
    if (isLoading) return;
    if (loadedScope !== `${clinicId}:${period}`) return;
    setTab(defaultDecision.tab);
    setDefaultReason(defaultDecision.reason);
  }, [explicitTab, isLoading, loadedScope, clinicId, period, defaultDecision]);

  const journey = useMemo(
    () =>
      buildPatientJourneySnapshot({
        tenantId: clinicId,
        period,
        pacientes,
        recurrencia,
        noVolvieron,
        canPacientes,
        canRecurrencia: canRecurrence,
        canNoVolvieron: canNoReturn,
      }),
    [clinicId, period, pacientes, recurrencia, noVolvieron, canPacientes, canRecurrence, canNoReturn]
  );

  const tabs = useMemo(() => {
    const items: Array<{ id: PatientsTab; label: string }> = [];
    if (multiClinic) {
      items.push({ id: 'network', label: t('domain.patients.tabs.network') });
    }
    items.push(
      { id: 'overview', label: t('domain.patients.tabs.overview') },
      { id: 'acquisition', label: t('domain.patients.tabs.acquisition') },
      { id: 'retention', label: t('domain.patients.tabs.retention') },
      { id: 'risk', label: t('domain.patients.tabs.risk') }
    );
    return items;
  }, [multiClinic, t]);

  const qs = `tenant=${clinicId}&period=${period}&year=${year}&month=${month}`;
  const handleStageSelect = (next: PatientJourneyDeepLinkTab) => handleTabChange(next);
  const activeTab = tab ?? (isNetworkMode ? 'network' : 'overview');
  const awaitingAutoTab = !explicitTab && !isNetworkMode && (isLoading || tab == null);
  const showAutoBanner =
    !bannerDismissed && (defaultReason === 'no_return' || defaultReason === 'recurrence_drop');
  const bannerText =
    defaultReason === 'no_return'
      ? t('domain.patients.defaultTab.noReturn', {
          count: formatInteger(noVolvieron?.total ?? 0, locale),
        })
      : defaultReason === 'recurrence_drop'
        ? t('domain.patients.defaultTab.recurrenceDrop', {
            delta: formatInteger(Math.abs(defaultDecision.recurrenceDeltaPp ?? 0), locale),
          })
        : null;

  if (!canView) {
    return <AccessDenied domain="patients" homeTo={firstVisibleHousePath(hasPermission)} />;
  }

  return (
    <div className="panel-page panel-safe-bottom space-y-5">
      <Breadcrumbs
        label={t('breadcrumbs.nav')}
        items={[
          {
            label: t('breadcrumbs.patients'),
            to: isNetworkMode ? undefined : breadcrumbHref('/patients', { period }),
          },
          ...(!isNetworkMode && clinicId
            ? [{ label: clinicNames[clinicId] ?? clinicId }]
            : []),
        ]}
      />
      <DomainPageHeader
        question={t('domain.patients.question')}
        title={t('domain.patients.title')}
        subtitle={t('domain.patients.subtitle', {
          period: periodLabel,
        })}
      />

      <div className="hidden md:block">
        <DomainScopeBar
          showClinic={!isNetworkMode}
          clinicId={clinicId}
          clinicOptions={clinicOptions}
          onClinicChange={handleClinicChange}
        />
      </div>

      {!isNetworkMode ? (
      <div className="space-y-2 md:hidden">
        <FilterSheet
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          fields={filterFields}
          values={{ clinic: clinicId }}
          onApply={(next) => {
            if (next.clinic) handleClinicChange(next.clinic);
          }}
          onClearAll={() => handleClinicChange(defaultClinicId)}
          emptyValues={{ clinic: defaultClinicId }}
        />
        <ActiveFilterChips chips={activeFilterChips} />
      </div>
      ) : null}

      <DomainTabs
        tabs={[...tabs]}
        active={awaitingAutoTab ? '' : activeTab}
        onChange={handleTabChange}
        ariaLabel={t('domain.patients.tabsLabel')}
      />

      {showAutoBanner && bannerText ? (
        <div
          className="flex max-w-2xl items-start justify-between gap-3 rounded-md border border-amber-200/80 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100"
          data-testid="patients-default-tab-banner"
          role="status"
        >
          <p>{bannerText}</p>
          <button
            type="button"
            className="shrink-0 text-xs font-semibold underline-offset-2 hover:underline"
            onClick={() => setBannerDismissed(true)}
          >
            {t('domain.patients.defaultTab.dismiss')}
          </button>
        </div>
      ) : null}

      {error && (
        <PanelPageState variant="error" layout="inline" onRetry={() => window.location.reload()} />
      )}

      {isLoading || awaitingAutoTab || (isNetworkMode && networkLoading) ? (
        <div className="rounded-xl border border-[var(--bi-line)] bg-white/80 dark:bg-white/5">
          <LoadingBlock minHeightClassName="min-h-[8rem]" />
        </div>
      ) : (
        <div className="space-y-4">
          {activeTab === 'network' && multiClinic && (
            <PatientsNetworkRanking
              rows={namedNetworkRows}
              locale={locale}
              onOpenClinic={handleOpenNetworkClinic}
            />
          )}
          {activeTab === 'overview' && (
            <PatientJourneyPipeline
              snapshot={journey}
              locale={locale}
              riskDesde={noVolvieron?.desde}
              riskHasta={noVolvieron?.hasta}
              onStageSelect={handleStageSelect}
            />
          )}

          {activeTab === 'acquisition' && pacientes && (
            <div className="space-y-2">
              <p className="text-xs text-slate-500 dark:text-slate-400">{t('domain.patients.newHint')}</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <MetricKpiCard
                  label={t('domain.patients.kpis.new')}
                  value={formatInteger(pacientes.nuevos, locale)}
                  tone="blue"
                  metricId="newPatients"
                />
                <MetricKpiCard
                  label={t('domain.patients.kpis.active')}
                  value={formatInteger(pacientes.activos, locale)}
                  tone="green"
                />
                <MetricKpiCard
                  label={t('domain.patients.kpis.seen')}
                  value={formatInteger(pacientes.atendidos, locale)}
                  tone="indigo"
                  metricId="patients"
                />
              </div>
            </div>
          )}

          {activeTab === 'retention' && recurrencia && (
            <div className="space-y-2">
              <p className="text-xs text-slate-500 dark:text-slate-400">{t('domain.patients.newInPeriodHint')}</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <MetricKpiCard
                  label={t('domain.patients.kpis.recurring')}
                  value={formatInteger(recurrencia.recurrentes, locale)}
                  tone="green"
                />
                <MetricKpiCard
                  label={t('domain.patients.kpis.conversion')}
                  value={formatPercent(recurrencia.tasaRecurrencia, locale)}
                  tone="green"
                  metricId="conversion"
                />
                <MetricKpiCard
                  label={t('domain.patients.kpis.newInPeriod')}
                  value={formatInteger(recurrencia.nuevos, locale)}
                  tone="blue"
                  metricId="newPatients"
                />
              </div>
            </div>
          )}

          {activeTab === 'risk' && noVolvieron && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <NoVolvieronExportControls
                  canExport={canExportRisk}
                  empty={noVolvieron.total === 0}
                  disabled={isLoading}
                  onExport={handleRiskExport}
                />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <MetricKpiCard
                  label={t('domain.patients.kpis.noReturn')}
                  value={formatInteger(noVolvieron.total, locale)}
                  tone="amber"
                  metricId="noReturn"
                />
                <MetricKpiCard
                  label={t('domain.patients.kpis.exhaustedPlan')}
                  value={formatInteger(noVolvieron.conPlanAgotado, locale)}
                  tone="red"
                />
              </div>
            </div>
          )}

          {activeTab === 'retention' && canRecurrence && clinicId && (
            <RecurrenceBlock tenantId={clinicId} year={year} month={month} />
          )}

          {activeTab === 'risk' && canNoReturn && clinicId && (
            <NoVolvieronBlock tenantId={clinicId} year={year} month={month} />
          )}

          {!isNetworkMode && !pacientes && !recurrencia && !noVolvieron && (
            <p className="text-sm text-gray-500">{t('domain.patients.empty')}</p>
          )}

          {!isNetworkMode && (
          <div className="flex flex-wrap gap-3 text-sm">
            {(activeTab === 'retention' || activeTab === 'overview') && (
              <Link
                to={`/reports/conversion?${qs}`}
                className="font-semibold text-teal-700 hover:underline dark:text-teal-300"
              >
                {t('domain.patients.links.conversion')} →
              </Link>
            )}
            {(activeTab === 'risk' || activeTab === 'overview') && (
              <Link
                to={`/reports/no-return?${qs}`}
                className="font-semibold text-teal-700 hover:underline dark:text-teal-300"
              >
                {t('domain.patients.links.noReturn')} →
              </Link>
            )}
            {activeTab === 'overview' && (
              <Link
                to={`/patients?${qs}&tab=acquisition`}
                className="font-semibold text-teal-700 hover:underline dark:text-teal-300"
              >
                {t('domain.patients.links.mix')} →
              </Link>
            )}
          </div>
          )}
        </div>
      )}
    </div>
  );
}
